import type { LotTrace, TraceRow } from '@morales/shared';
import { useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { StockNav } from '../components/StockNav';
import { Empty, ErrorBox, PageHead, QueryView } from '../components/ui';
import { addDays, formatDate, formatMoney, formatQty, formatStamp, todayIso } from '../format';
import { useSearchParam } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';
import { useSite } from '../sites';

type Detector = { detect: (source: ImageBitmapSource) => Promise<{ rawValue: string }[]> };
type DetectorCtor = new (opts: { formats: string[] }) => Detector;
const detectorCtor = (): DetectorCtor | null => (typeof window !== 'undefined' && 'BarcodeDetector' in window ? ((window as unknown as { BarcodeDetector: DetectorCtor }).BarcodeDetector) : null);

/** Point the camera at a lot label (or type the code) to open the lot's story. */
export function ScanPage() {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canScan = detectorCtor() !== null && typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  useEffect(() => {
    if (!canScan) return;
    let stop = false;
    let stream: MediaStream | null = null;
    const Ctor = detectorCtor()!;
    const detector = new Ctor({ formats: ['qr_code', 'code_128'] });
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        const video = videoRef.current;
        if (!video || stop) return;
        video.srcObject = stream;
        await video.play();
        const tick = async () => {
          if (stop || !videoRef.current) return;
          try {
            const found = await detector.detect(videoRef.current);
            const hit = found.find((f) => f.rawValue.trim());
            if (hit) {
              stop = true;
              navigate(`/stock/trace/${encodeURIComponent(hit.rawValue.trim())}`);
              return;
            }
          } catch {
            // a frame that could not be read: try the next one
          }
          setTimeout(() => void tick(), 250);
        };
        void tick();
      } catch (err) {
        setCameraError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      stop = true;
      stream?.getTracks().forEach((tr) => tr.stop());
    };
  }, [canScan, navigate]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (code.trim()) navigate(`/stock/trace/${encodeURIComponent(code.trim())}`);
  };
  return (
    <div className="stack">
      <section className="stack-sm">
        <h1>{t('scan')}</h1>
        <StockNav />
        <p className="muted">{t('scanHint')}</p>
      </section>
      {canScan && !cameraError ? <video ref={videoRef} className="scanner" muted playsInline aria-label={t('camera')} /> : <p className="note">{cameraError ? `${t('cameraOff')}: ${cameraError}` : t('noCamera')}</p>}
      <form className="picker-head" onSubmit={submit}>
        <input className="search" type="text" autoComplete="off" autoCapitalize="characters" placeholder="ONI-YEL-50LB-261001-7" aria-label={t('lotCode')} value={code} onChange={(e) => setCode(e.target.value)} />
        <button type="submit" className="btn btn-primary" disabled={!code.trim()}>
          {t('open')}
        </button>
      </form>
    </div>
  );
}

/** The lot's story: where it came from, where it sits, who got it, what was lost, where it moved. */
export function TracePage() {
  const { t, intlLocale } = useLocale();
  const { code = '' } = useParams();
  const query = useQuery({ queryKey: qk.lotTrace(code), queryFn: ({ signal }) => api.lotTrace(code, signal) });
  return (
    <div className="stack">
      <PageHead title={code} back="/stock/scan" />
      <QueryView query={query}>
        {(x: LotTrace) => (
          <>
            <section className="card">
              <h2>{x.lot.itemName}</h2>
              <dl className="facts plain">
                <div className="fact">
                  <dt>{t('onHand')}</dt>
                  <dd data-testid="trace-on-hand">
                    {formatQty(x.lot.onHandPacks)} {x.lot.unit ?? ''} · {x.lot.locationName}
                    {x.lot.siteName ? ` (${x.lot.siteName})` : ''}
                  </dd>
                </div>
                <div className="fact">
                  <dt>{t('received')}</dt>
                  <dd>
                    {formatQty(x.lot.qtyReceived)} · {formatStamp(x.lot.receivedAt, intlLocale)} · {x.lot.ageDays} {t('ageDays')}
                  </dd>
                </div>
                <div className="fact">
                  <dt>{t('vendor')}</dt>
                  <dd>
                    {x.lot.vendorName ?? '—'}
                    {x.lot.documentRef ? ` · ${x.lot.documentRef}` : ''}
                    {x.lot.vendorLot ? ` · ${t('vendorLot')} ${x.lot.vendorLot}` : ''}
                  </dd>
                </div>
                {x.lot.origin || x.lot.grower ? (
                  <div className="fact">
                    <dt>{t('origin')}</dt>
                    <dd>{[x.lot.origin, x.lot.grower].filter(Boolean).join(' · ')}</dd>
                  </div>
                ) : null}
                {x.lot.fromLotCode ? (
                  <div className="fact">
                    <dt>{t('cameFrom')}</dt>
                    <dd>
                      <Link to={`/stock/trace/${encodeURIComponent(x.lot.fromLotCode)}`}>{x.lot.fromLotCode}</Link> · #{x.lot.transferNo}
                    </dd>
                  </div>
                ) : null}
                {x.lot.landedCost !== null ? (
                  <div className="fact">
                    <dt>{t('landed')}</dt>
                    <dd>{formatMoney(x.lot.landedCost, intlLocale)}</dd>
                  </div>
                ) : null}
              </dl>
            </section>
            <section className="card stack-sm">
              <h2>
                {t('wentTo')} <span className="label">{formatQty(x.soldPacks)}</span>
              </h2>
              {x.sales.length === 0 ? (
                <Empty>{t('notSoldYet')}</Empty>
              ) : (
                <ul className="list plain" data-testid="trace-sales">
                  {x.sales.map((s) => (
                    <li key={`${s.invoiceId}`} className="row">
                      <span className="row-main">
                        <span className="row-title">
                          <Link to={`/customers/${s.customerId}`}>{s.customerName}</Link>
                          {s.status === 'void' ? <span className="badge">{t('voided')}</span> : s.status === 'deleted' ? <span className="badge">{t('deletedTag')}</span> : null}
                        </span>
                        <span className="row-sub">
                          {s.status === 'deleted' ? <>#{s.invoiceNo}</> : <Link to={`/invoices/${s.invoiceId}`}>#{s.invoiceNo}</Link>} · {formatDate(s.invoiceDate, intlLocale)}
                        </span>
                      </span>
                      <span className="row-end">
                        <span className="row-end-value">{formatQty(s.qty)}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            {x.transfers.length > 0 ? (
              <section className="card stack-sm">
                <h2>
                  {t('transfers')} <span className="label">{formatQty(x.transferredPacks)}</span>
                </h2>
                <ul className="list plain">
                  {x.transfers.map((tr) => (
                    <li key={tr.transferNo} className="row">
                      <span className="row-main">
                        <span className="row-title">
                          #{tr.transferNo} → {tr.toSiteName}
                        </span>
                        <span className="row-sub">
                          {formatStamp(tr.at, intlLocale)}
                          {tr.toLotCode ? (
                            <>
                              {' · '}
                              <Link to={`/stock/trace/${encodeURIComponent(tr.toLotCode)}`}>{tr.toLotCode}</Link>
                            </>
                          ) : null}
                        </span>
                      </span>
                      <span className="row-end">
                        <span className="row-end-value">{formatQty(tr.qty)}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            {x.shrink.length > 0 ? (
              <section className="card stack-sm">
                <h2>
                  {t('shrinkByCause')} <span className="label">{formatQty(x.shrinkPacks)}</span>
                </h2>
                <ul className="list plain">
                  {x.shrink.map((m, i) => (
                    <li key={i} className="row">
                      <span className="row-main">
                        <span className="row-title">{m.reason ? t(`shrink_${m.reason}` as 'shrink_age') : t('shrink_other')}</span>
                        <span className="row-sub">
                          {formatStamp(m.at, intlLocale)}
                          {m.note ? ` · ${m.note}` : ''}
                        </span>
                      </span>
                      <span className="row-end">
                        <span className="row-end-value">{formatQty(m.qty)}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </>
        )}
      </QueryView>
    </div>
  );
}

/** One QR per lot, drawn into a data URL once. */
function Qr({ text }: { text: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    QRCode.toDataURL(text, { margin: 0, width: 160, errorCorrectionLevel: 'M' })
      .then((u) => {
        if (live) setUrl(u);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [text]);
  return url ? <img src={url} alt={text} width={80} height={80} /> : <span className="qr-blank" />;
}

/** Labels to print: one per lot with stock (or every lot of one receipt). The phone's share sheet saves a PDF. */
export function LabelsPage() {
  const { t, intlLocale } = useLocale();
  const { siteId } = useSite();
  const [receipt] = useSearchParam('receipt');
  const lots = useQuery({ queryKey: qk.lots(`labels:${receipt}:${siteId ?? ''}`), queryFn: ({ signal }) => api.lots({ ...(siteId ? { site: siteId } : {}), ...(receipt ? { all: 'true' } : {}) }, signal) });
  return (
    <div className="stack labels">
      <PageHead title={t('labels')} back="/stock/lots" />
      <p className="muted no-print">{t('labelsHint')}</p>
      <button type="button" className="btn btn-primary btn-block no-print" onClick={() => window.print()}>
        {t('print')}
      </button>
      <QueryView query={lots}>
        {(rows) => {
          const shown = receipt ? rows.filter((l) => l.lotCode) : rows;
          return shown.length === 0 ? (
            <Empty>{t('noLots')}</Empty>
          ) : (
            <ul className="label-sheet" data-testid="labels">
              {shown.map((l) => (
                <li key={l.id} className="lot-label">
                  <Qr text={l.lotCode} />
                  <div className="lot-label-text">
                    <strong>{l.itemName}</strong>
                    <span className="lot-label-code">{l.lotCode}</span>
                    <span>
                      {formatStamp(l.receivedAt, intlLocale)} · {formatQty(l.qtyReceived)} {l.unit ?? ''}
                      {l.vendorName ? ` · ${l.vendorName}` : ''}
                      {l.origin ? ` · ${l.origin}` : ''}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          );
        }}
      </QueryView>
    </div>
  );
}

/** The trace export as a spreadsheet: one row per invoice line with its lot. */
export function traceCsv(rows: TraceRow[]): string {
  const esc = (v: string | number | null) => (v === null ? '' : /[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
  return ['lot,item,vendor,origin,grower,vendor_lot,received,invoice_no,invoice_date,customer,qty,unit', ...rows.map((r) => [r.lotCode, r.itemName, r.vendorName, r.origin, r.grower, r.vendorLot, r.receivedAt, r.invoiceNo, r.invoiceDate, r.customerName, r.qty, r.unit].map(esc).join(','))].join('\n');
}

export function TraceExportPage() {
  const { t, intlLocale } = useLocale();
  const [from, setFrom] = useState(addDays(todayIso(), -30));
  const [to, setTo] = useState(todayIso());
  const [lot, setLot] = useState('');
  const key = `${from}:${to}:${lot.trim()}`;
  const query = useQuery({ queryKey: qk.traceExport(key), queryFn: ({ signal }) => api.traceExport({ from, to, ...(lot.trim() ? { lot: lot.trim() } : {}) }, signal) });
  const download = (rows: TraceRow[]) => {
    const blob = new Blob([traceCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trace-${from}-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="stack">
      <PageHead title={t('traceExport')} back="/stock/lots" />
      <p className="muted">{t('traceExportHint')}</p>
      <div className="two-up">
        <label className="field">
          <span>{t('dateFrom')}</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="field">
          <span>{t('dateTo')}</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
      </div>
      <label className="field">
        <span>{t('lotCode')}</span>
        <input type="text" value={lot} onChange={(e) => setLot(e.target.value)} autoCapitalize="characters" autoComplete="off" />
      </label>
      {query.isError ? <ErrorBox error={query.error} /> : null}
      <QueryView query={query}>
        {(rows) => (
          <>
            <button type="button" className="btn btn-primary btn-block" disabled={rows.length === 0} onClick={() => download(rows)}>
              {t('download')} ({rows.length})
            </button>
            {rows.length === 0 ? (
              <Empty>{t('noResults')}</Empty>
            ) : (
              <ul className="list" data-testid="trace-rows">
                {rows.slice(0, 200).map((r, i) => (
                  <li key={i} className="row">
                    <span className="row-main">
                      <span className="row-title">
                        {r.customerName} <span className="label">#{r.invoiceNo}</span>
                      </span>
                      <span className="row-sub">
                        {formatDate(r.invoiceDate, intlLocale)} · {r.itemName} · <Link to={`/stock/trace/${encodeURIComponent(r.lotCode)}`}>{r.lotCode}</Link>
                        {r.origin ? ` · ${r.origin}` : ''}
                      </span>
                    </span>
                    <span className="row-end">
                      <span className="row-end-value">{formatQty(r.qty)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </QueryView>
    </div>
  );
}
